import { ChangeEvent, useEffect, useState } from "react";

import { Link } from "react-router-dom";

import { appInsights } from "../../../config/appInsights";

import Paginator from "../../Pagination/Pagination.component";
import CustomSelectDropdown from "../../CustomHTMLElements/CustomSelectDropdown";
import ButtonLoader, { Loader } from "../../../UI/Loaders/Loaders";
import BackButtonComponent from "../../../NewComponents/BackButton/BackButton.component";
import useLoanApplications, {
  TQuery,
} from "../../../custom-hooks/usePendingLoanApplications";
import useLoanApplicationStatus from "../../../custom-hooks/useLoanApplicationStatus";

import styles from "./LoanApplications.module.scss";

const LoanApplications = () => {
  const [pageNumber, setPageNumber] = useState(1);
  const [size, setSize] = useState(10);

  const [searchQuery, setSearchQuery] = useState({
    status: `status=`,
  });
  const [query, setQuery] = useState<TQuery>({
    Page: 1,
    PageSize: 10,
    search_value: "",
    start_date: "",
    end_date: "",
    application_type_id: "",
    core_banking_tenant: "",
    status: `status=`,
  });

  useEffect(() => {
    appInsights.trackPageView({
      name: "Loan Applications  - (LoanApplications.tsx)",
      isLoggedIn: true,
    });
  }, []);

  const {
    data: pendingLoanApplications,
    status,
    error,
  } = useLoanApplications({
    Page: pageNumber,
    PageSize: size,
    end_date: query?.end_date,
    search_value: query?.search_value,
    start_date: query?.start_date,
    status: query?.status,
    application_type_id: query?.application_type_id,
    core_banking_tenant: query?.core_banking_tenant,
  });
  const { data: applicationStatus } = useLoanApplicationStatus();
  const handleSearch = () => {
    const _query = searchQuery as TQuery;
    setPageNumber(1);
    return setQuery(_query);
  };

  const handleChange = (
    e: ChangeEvent<HTMLSelectElement | HTMLInputElement>,
  ) => {
    if (e.target.name !== "status") {
      setSearchQuery((prev) => {
        return {
          ...prev,
          [e.target.name]: e.target.value,
        };
      });
    } else {
      setSearchQuery((prev) => {
        return {
          ...prev,
          [e.target.name]: `status=${e.target.value}`,
        };
      });
    }
  };

  let RenderedComponent = null;
  if (!pendingLoanApplications && status !== "loading") {
    RenderedComponent = (
      <div>
        <div className="grid__padding animated fadeInRight">
          <h4 className="center-txt">
            <i className="fas fa-bell" /> Unable to Fetch Loan Applications
          </h4>
        </div>
      </div>
    );
  } else if (status === "loading") {
    RenderedComponent = (
      <Loader centered={true} text="Loading Pending Loan Application..." />
    );
  } else {
    if (error) {
      RenderedComponent = (
        <div>
          <div className="grid__padding animated fadeInRight">
            <h4 className="center-txt p-y-5">
              <i className="fas fa-bell" /> There was an issue while fetching
              Loan Applications, Please Reload The Page{" "}
            </h4>
          </div>
        </div>
      );
    } else if (
      (pendingLoanApplications &&
        pendingLoanApplications?.data &&
        pendingLoanApplications?.data.length === 0) ||
      (pendingLoanApplications && !pendingLoanApplications?.data)
    ) {
      RenderedComponent = (
        <div className="centerLoader">
          <div className="grid__padding animated fadeInRight">
            <h4 className="center-txt p-y-5">
              <i className="fas fa-bell" /> There are currently no pending
              applications.
            </h4>
          </div>
        </div>
      );
    } else {
      RenderedComponent = (
        <div className="row m-x-sm m-y-2 grid__padding">
          <div className="table-responsive">
            <table className="table">
              <thead className="bg-lighter-grey">
                <tr>
                  <th>S/N</th>
                  <th>Customer Name</th>
                  <th>Customer Email</th>
                  <th>Status</th>
                  <th>Loan Type</th>
                  <th>Product Type </th>
                  <th>Reference</th>
                  <th>Application Date</th>
                  <th>Approval Date</th>
                  <th>View Details</th>
                </tr>
              </thead>
              <tbody>
                {pendingLoanApplications &&
                  pendingLoanApplications?.data?.length > 0 &&
                  pendingLoanApplications.data?.map((request, _id) => {
                    const {
                      status,
                      ref_name,
                      application_date,
                      approval_date,
                      id,
                      funder_name,
                      customer_name,
                      funder_email,
                      funder_id,
                      application_type,
                      application_type_id,
                      loan_application_details,
                    } = request;
                    return (
                      <tr key={_id}>
                        <td>{(pageNumber - 1) * size + (_id + 1)}</td>
                        <td>{customer_name ? customer_name : ""}</td>
                        <td>{funder_email}</td>
                        <td>{status}</td>
                        <td>{application_type}</td>
                        <td>
                          {loan_application_details.length > 0
                            ? loan_application_details[0]
                                ?.proposed_loan_product_category_name
                            : ""}
                        </td>
                        <td>{ref_name}</td>
                        <td>
                          {application_date &&
                            application_date.substring(0, 10)}
                        </td>
                        <td>
                          {approval_date
                            ? approval_date.substring(0, 10)
                            : "NIL"}
                        </td>
                        <td>
                          <Link
                            to={{
                              pathname: `/loan-applications-readonly/${id}`,
                              state: {
                                customerId: funder_id,
                                id,
                                application_type,
                                application_type_id,
                                ref_name,
                                title: "Loan Application",
                                isViewOnly: true,
                              },
                            }}
                            className="color-blue"
                          >
                            View Details
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      );
    }
  }
  return (
    <div className="speed-1x animated fadeInRight">
      <h3 className="page-title d-flex align-items-center mb-3">
        <BackButtonComponent /> Loan Applications
      </h3>

      <div className={`row p-l-1 ${styles.ActionBar}`}>
        <div className="form-group">
          <span
            style={{
              display: "inline-block",
              marginTop: "0.5rem",
              marginRight: "0.5rem",
            }}
          >
            Customer Name:
          </span>
          <input
            type="text"
            name="search_value"
            className="form-control"
            placeholder="customer name"
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <span
            style={{
              display: "inline-block",
              marginTop: "0.5rem",
              marginRight: "0.5rem",
            }}
          >
            Reference:
          </span>
          <input
            type="text"
            name="search_value"
            className="form-control"
            placeholder="reference"
            onChange={handleChange}
          />
        </div>
        <div className="form-group">
          <span
            style={{
              display: "inline-block",
              marginTop: "0.5rem",
              marginRight: "0.5rem",
            }}
          >
            Loan Type{" "}
          </span>
          <div>
            <CustomSelectDropdown
              onChange={handleChange}
              name="application_type_id"
            >
              <option value="">All</option>
              <option value={1}>Spark</option>
              <option value={2}>Embed</option>
            </CustomSelectDropdown>
          </div>
        </div>
        <div className="form-group">
          <span
            style={{
              display: "inline-block",
              marginTop: "0.5rem",
              marginRight: "0.5rem",
            }}
          >
            Company
          </span>
          <div>
            <CustomSelectDropdown
              onChange={handleChange}
              name="core_banking_tenant"
            >
              <option value="">All</option>
              <option value="default">Advancly Tech</option>
              <option value="advanclymfb">Advancly MFB</option>
            </CustomSelectDropdown>
          </div>
        </div>
        <div className="form-group">
          <span
            style={{
              display: "inline-block",
              marginTop: "0.5rem",
              marginRight: "0.5rem",
            }}
          >
            Status
          </span>
          <div>
            <CustomSelectDropdown onChange={handleChange} name="status">
              <option value="">All</option>

              {applicationStatus &&
                applicationStatus?.map((_status: any, index: number) => (
                  <option key={index} value={_status?.id}>
                    {_status?.name}
                  </option>
                ))}
            </CustomSelectDropdown>
          </div>
        </div>

        <div className="form-group">
          <span
            style={{
              display: "inline-block",
              marginTop: "0.5rem",
              marginRight: "0.5rem",
            }}
          >
            Start:
          </span>
          <input
            type="date"
            name="start_date"
            className="form-control"
            placeholder="start"
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <span
            style={{
              display: "inline-block",
              marginTop: "0.5rem",
              marginRight: "0.5rem",
            }}
          >
            End:
          </span>
          <input
            type="date"
            name="end_date"
            className="form-control"
            placeholder="end"
            onChange={handleChange}
          />
        </div>
        <div className="form-group">
          <span
            style={{
              display: "inline-block",
              marginTop: "0.5rem",
              marginRight: "0.5rem",
            }}
          >
            Number of Results:
          </span>
          <div>
            <CustomSelectDropdown
              onChange={(e) => setSize(Number(e.target.value))}
              value={size}
              name=""
            >
              <option value="">All</option>
              <option value={10}>10 Entries</option>
              <option value={20}>20 Entries</option>
              <option value={50}>50 Entries</option>
              <option value={100}>100 Entries</option>
            </CustomSelectDropdown>
          </div>
        </div>

        <div className="d-flex justify-content-end">
          <button
            className="btn advancly-btn btn-md"
            disabled={status === "loading"}
            onClick={handleSearch}
          >
            Search{status === "loading" && <ButtonLoader />}
          </button>
        </div>
      </div>

      {RenderedComponent}
      <Paginator
        size={size}
        page={pageNumber}
        count={pendingLoanApplications?.totalCount}
        changeCurrentPage={(pageNumber: number) => setPageNumber(pageNumber)}
      />
    </div>
  );
};

export default LoanApplications;
